import React, { useState, useRef, useEffect } from "react";
import { Editor } from "@tiptap/react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Toolbar as MuiToolbar,
  Button,
  IconButton,
  Divider,
  Typography,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControlLabel,
  DialogActions,
  Checkbox,
} from "@material-ui/core";
// Native Material-UI Icons
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatStrikethroughIcon from "@material-ui/icons/FormatStrikethrough";
import CodeIcon from "@material-ui/icons/Code";
import LinkIcon from "@material-ui/icons/Link";
import ImageIcon from "@material-ui/icons/Image";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import SaveIcon from "@material-ui/icons/Save";
import SettingsIcon from "@material-ui/icons/Settings";
import GetAppIcon from "@material-ui/icons/GetApp";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import TableChartIcon from "@material-ui/icons/TableChart";
import PrintIcon from "@material-ui/icons/Print";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import AddToPhotosIcon from "@material-ui/icons/AddToPhotos";
import { SketchPicker } from "react-color";
import FormatAlignJustifyIcon from "@material-ui/icons/FormatAlignJustify";
import { toast } from "react-toastify";
import { useFetchContext } from "../../../../common/FetchContext";
import { UseTiptapImageUpdate } from "../../../../common/services/fileServices";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import { CellSelection, TableMap } from "@tiptap/pm/tables";
import TableCell from "@tiptap/extension-table-cell";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import CallToActionIcon from "@material-ui/icons/CallToAction";
import AddCircleOutlinedIcon from "@material-ui/icons/AddCircleOutlined";
import FormatLineSpacingIcon from "@material-ui/icons/FormatLineSpacing";
import HighlightIcon from "@material-ui/icons/Highlight";
import FindReplaceIcon from "@material-ui/icons/FindReplace";
import TocIcon from "@material-ui/icons/Toc";
import FormatIndentIncreaseIcon from "@material-ui/icons/FormatIndentIncrease";
import FormatIndentDecreaseIcon from "@material-ui/icons/FormatIndentDecrease";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: "#121212",
    borderBottom: "1px solid #27272a",
    color: "#a1a1aa",
    padding: "6px 12px",
    display: "flex",
    alignItems: "center",
    position: "sticky", // Changed from relative to sticky or static if preferred, but static lets it flow naturally with page layout
    top: 0,
    zIndex: 1000, // Ensures toolbar stays above document content
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    overflow: "visible",
    whiteSpace: "normal",
    // MUI's own `.MuiToolbar-root` styles (applied alongside this custom
    // class since `<MuiToolbar>` merges rather than replaces its internal
    // class) default to `flex-wrap: nowrap` with no override. Depending on
    // CSS injection order that can win over the plain `flexWrap: "wrap"`
    // below, silently disabling wrapping — at narrow viewport widths the
    // later toolbar icons (including image size/alignment, alignment,
    // lists, etc.) then render past the right edge of the screen with no
    // way to reach them. Forcing it with `!important` guarantees wrapping
    // regardless of injection order.
    flexWrap: "wrap !important" as any,
    rowGap: 6,
    columnGap: 4,
  },
  leftGroup: {
    display: "contents", // was a flex box — now its buttons flow as direct children of the toolbar
  },
  disabledIconBtn: {
    opacity: 0.35,
    cursor: "not-allowed",
  },

  rightGroup: {
    display: "contents", // same — its icons now pack in wherever room is left, instead of a forced new line
  },
  iconBtn: {
    color: "#ffffff",
    padding: "6px",
    height: 32,
    width: 32,
    boxSizing: "border-box",
    borderRadius: "4px",
    alignSelf: "center",

    "&:hover": {
      backgroundColor: "#27272a",
      color: "#ffffff",
    },

    "&.Mui-disabled": {
      color: "#ffffff",
      opacity: 1,
    },
  },

  activeIconBtn: {
    backgroundColor: "#3f3f46",
    color: "#ffffff",

    "&:hover": {
      backgroundColor: "#3f3f46",
    },
  },
  textMenuBtn: {
    color: "#d4d4d8",
    fontSize: "13px",
    textTransform: "none",
    padding: "4px 8px",
    height: 32, // added — matches iconBtn's height exactly
    boxSizing: "border-box",
    minWidth: "auto",
    alignSelf: "center", // added
    fontFamily: "system-ui, sans-serif",
    "&:hover": {
      backgroundColor: "#27272a",
      color: "#ffffff",
    },
  },
  divider: {
    backgroundColor: "#27272a",
    margin: "0 8px",
    height: "18px",
  },
  zoomText: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#d4d4d8",
    minWidth: "36px",
    textAlign: "center",
    fontFamily: "system-ui, sans-serif",
  },
  dropdownMenu: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    backgroundColor: "#18181b",
    border: "1px solid #27272a",
    borderRadius: "4px",
    minWidth: "130px",
    zIndex: 99999,
    maxHeight: "60vh",
    overflowY: "auto",
  },
  // Added to match your existing dropdown design inside a portal-safe MUI Menu
  menuPaper: {
    backgroundColor: "#18181b !important",
    border: "1px solid #27272a !important",
    borderRadius: "4px !important",
    minWidth: "130px",
    boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.4) !important",
    padding: "4px 0 !important",
  },
  menuItem: {
    padding: "8px 12px !important",
    minHeight: "32px !important",
    boxSizing: "border-box",
    color: "#d4d4d8 !important",
    fontSize: "13px !important",
    fontWeight: 400,
    lineHeight: "16px !important",
    fontFamily: "system-ui, sans-serif !important",

    "&:hover": {
      backgroundColor: "#27272a !important",
      color: "#ffffff !important",
    },
  },
  dropdownItem: {
    padding: "8px 12px",
    color: "#d4d4d8",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "13px",
    fontFamily: "system-ui, sans-serif",
    "&:hover": {
      backgroundColor: "#27272a",
      color: "#ffffff",
    },
  },
  colorDropdownMenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    marginTop: "4px",
    zIndex: 10,
    padding: "10px",
    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.25)",
    "& .sketch-picker": {
      background: "transparent !important",
      boxShadow: "none !important",
      padding: "0px !important",
    },
  },

  "@global": {
    ".ProseMirror table": {
      borderCollapse: "collapse",
      // CHANGED: "auto" layout allows rows and columns to fluidly calculate dynamic heights!
      tableLayout: "auto",
      width: "100%",
      margin: "0",
      overflow: "auto",
    },
    ".ProseMirror td, .ProseMirror th": {
      minWidth: "1em",
      border: "1px solid #3f3f46",
      padding: "6px 8px",
      verticalAlign: "top",
      boxSizing: "border-box",
      position: "relative",
      // ADDED: Prevents the cell wrapper from ignoring structural adjustments during mouse movement
      height: "auto",
      overflow: "visible",
    },
    ".ProseMirror tr": {
      // ADDED: Forces the table rows to obey style="height: ...px" injected by JavaScript
      boxSizing: "border-box",
    },
    // Ensure the pointer-events target elements accurately capture clicks
    ".ProseMirror td::after, .ProseMirror th::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: "-4px",
      height: "8px",
      background: "transparent",
      opacity: 0,
      cursor: "row-resize !important",
      // INCREASED: Ensures the blue handle sits above all text layers to capture the mousedown trigger immediately
      zIndex: 99,
      pointerEvents: "auto !important",
    },
    // Highlight the handle bar actively on element hover
    ".ProseMirror td:hover::after, .ProseMirror th:hover::after": {
      opacity: 1,
    },
    ".ProseMirror .column-resize-handle": {
      position: "absolute",
      right: "-2px",
      top: 0,
      bottom: "-2px",
      width: "4px",
      opacity: 0,
      cursor: "col-resize !important",
      zIndex: 10,
    },
    ".ProseMirror .selectedCell:after": {
      zIndex: 2,
      position: "absolute",
      content: '""',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      background: "rgba(59, 130, 246, 0.1)",
      pointerEvents: "none",
    },
  },
  menuItemHeader: {
    padding: "6px 12px 2px 12px !important",
    minHeight: "24px !important",
    boxSizing: "border-box",
    fontSize: "11px !important",
    lineHeight: "14px !important",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em !important",
    color: "#71717a !important",
    opacity: "1 !important",
    cursor: "default !important",
    fontFamily: "system-ui, sans-serif !important",

    "&:hover": {
      backgroundColor: "transparent !important",
    },
  },
  menuDivider: {
    backgroundColor: "#27272a !important",
    margin: "4px 0 !important",
    height: "1px",
  },
}));

type Props = {
  editor: Editor | null;
  onSave: () => void;
  onPrint: () => void;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  darkMode: boolean;
  toggleTheme: () => void;
  workorder: number;
  onInsertButton: () => void;
  onOpenFindReplace: () => void;
  onOpenNavPane?: () => void;
  onDeletePage: () => void;
  onConfirmDeletePage: () => void;
  currentPageNumber: number;
};

type MenuType =
  | "text"
  | "font"
  | "fontSize"
  | "lineHeight"
  | "headerDirection"
  | "letterSpacing"
  | null;
export const CustomTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),

      backgroundColor: {
        default: null,

        parseHTML: (element) => {
          return element.getAttribute("data-background-color");
        },

        renderHTML: (attributes) => {
          if (!attributes.backgroundColor) {
            return {};
          }

          return {
            "data-background-color": attributes.backgroundColor,
            style: `background-color: ${attributes.backgroundColor}`,
          };
        },
      },
    };
  },
});

export default function TipTapToolbar({
  editor,
  onSave,
  onPrint,
  zoom,
  setZoom,
  darkMode,
  toggleTheme,
  workorder,
  onInsertButton,
  onOpenFindReplace,
  onOpenNavPane,
  onDeletePage,
  onConfirmDeletePage,
  currentPageNumber,
}: Props) {
  const classes = useStyles();

  // Single state to track which manual dropdown is currently open
  const [activeMenu, setActiveMenu] = useState<MenuType>(null);
  const { mutateAsync: mutateAsset } = UseTiptapImageUpdate();

  const textMenuRef = useRef<HTMLDivElement>(null);
  const fontMenuRef = useRef<HTMLDivElement>(null);
  const fontSizeMenuRef = useRef<HTMLDivElement>(null);
  const lineHeightMenuRef = useRef<HTMLDivElement>(null);
  const headerDirectionMenuRef = useRef<HTMLDivElement>(null);
  const letterSpacingMenuRef = useRef<HTMLDivElement>(null);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [openInNewTab, setOpenInNewTab] = useState(true);
  const [savedCellSelection, setSavedCellSelection] =
    useState<CellSelection | null>(null);

  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [pickerColor, setPickerColor] = useState("#000000");
  const [bgColorAnchorEl, setBgColorAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [bgPickerColor, setBgPickerColor] = useState("#ffffff");
  const [tableAnchorEl, setTableAnchorEl] = useState<null | HTMLElement>(null);
  const [imageAnchorEl, setImageAnchorEl] = useState<null | HTMLElement>(null);
  const [isImageSelected, setIsImageSelected] = useState(false);
  const [textBgColorAnchorEl, setTextBgAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [textBgColor, setTextBgColor] = useState("#ffff00");
  const savedCellSelectionRef = useRef<CellSelection | null>(null);

  const handleColorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActiveMenu(null); // Close other text dropdowns when color popover opens
    setColorAnchorEl(event.currentTarget);
    if (editor) {
      setPickerColor(editor.getAttributes("textStyle")?.color || "#000000");
    }
  };

  const handleAlignment = (alignment: "left" | "center" | "right") => {
    if (!editor) return;
    if (editor.isActive("image")) {
      editor.chain().focus().updateAttributes("image", { alignment }).run();
    } else {
      editor.chain().focus().setTextAlign(alignment).run();
    }
  };

  const isAligned = (alignment: "left" | "center" | "right") => {
    if (!editor) return false;
    if (editor.isActive("image")) {
      return editor.getAttributes("image").alignment === alignment;
    }
    return editor.isActive({ textAlign: alignment });
  };

  const handleBgColorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!editor) return;

    const selection = editor.state.selection;

    if (!(selection instanceof CellSelection)) {
      console.log("Please select a table column/cells first");
      return;
    }

    /*
     * Save the exact CellSelection before
     * opening the color picker.
     */
    savedCellSelectionRef.current = selection;

    setBgColorAnchorEl(event.currentTarget);
  };

  const saveCurrentCellSelection = () => {
    if (!editor) return;

    const { selection } = editor.state;

    if (selection instanceof CellSelection) {
      savedCellSelectionRef.current = selection;
    }
  };
  const handleTextBgColorClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setActiveMenu(null);
    setTextBgAnchorEl(event.currentTarget);
    const currentHighlight = editor?.getAttributes("highlight")?.color;
    if (currentHighlight) {
      setTextBgColor(currentHighlight);
    }
  };

  const handleBgColorClose = () => {
    setBgColorAnchorEl(null);
  };

  const handleTextBgColorClose = () => {
    setTextBgAnchorEl(null);
  };

  const applyTableBackgroundColor = (color: string | null) => {
    if (!editor) return;

    const { state, view } = editor;

    /*
     * Get the current CellSelection.
     *
     * If the color picker has taken the selection away,
     * use the saved CellSelection.
     */
    const selection =
      state.selection instanceof CellSelection
        ? state.selection
        : savedCellSelectionRef.current;

    if (!selection) {
      return;
    }

    const anchorCell = selection.$anchorCell;
    const headCell = selection.$headCell;

    const tableDepth = anchorCell.depth - 1;
    const table = anchorCell.node(tableDepth);
    const tableStart = anchorCell.start(tableDepth);

    const map = TableMap.get(table);

    const anchorRect = map.findCell(anchorCell.pos - tableStart);

    const headRect = map.findCell(headCell.pos - tableStart);

    const startRow = Math.min(anchorRect.top, headRect.top);

    const endRow = Math.max(anchorRect.bottom, headRect.bottom);

    const startColumn = Math.min(anchorRect.left, headRect.left);

    const endColumn = Math.max(anchorRect.right, headRect.right);

    /*
     * Create one transaction.
     */
    let tr = state.tr;

    /*
     * Apply background color to all selected cells.
     */
    for (let row = startRow; row < endRow; row++) {
      for (let column = startColumn; column < endColumn; column++) {
        const cellPos = map.positionAt(row, column, table);

        const cellNode = table.nodeAt(cellPos);

        if (!cellNode) continue;

        tr = tr.setNodeMarkup(tableStart + cellPos, undefined, {
          ...cellNode.attrs,
          backgroundColor: color,
        });
      }
    }

    /*
     * IMPORTANT:
     *
     * Keep CellSelection.
     *
     * Do NOT convert it to TextSelection.
     */
    const mappedAnchor = tr.mapping.map(selection.$anchorCell.pos);

    const mappedHead = tr.mapping.map(selection.$headCell.pos);

    const restoredSelection = new CellSelection(
      tr.doc.resolve(mappedAnchor),
      tr.doc.resolve(mappedHead),
    );

    /*
     * Keep the CellSelection active.
     */
    tr = tr.setSelection(restoredSelection);

    /*
     * Dispatch the transaction.
     */
    view.dispatch(tr);

    /*
     * Save the selection again.
     *
     * This allows another color to be applied
     * to the same selected cells.
     */
    savedCellSelectionRef.current = restoredSelection;

    /*
     * IMPORTANT:
     *
     * Do NOT call:
     *
     * view.dom.blur()
     *
     * We want the CellSelection overlay to remain visible.
     */
    view.focus();
  };
  const applyTextHighlight = (color: string | null) => {
    if (!editor) return;

    if (color) {
      // Apply custom highlight color to selected text
      editor.chain().focus().setHighlight({ color }).run();
    } else {
      // Remove highlight from selected text
      editor.chain().focus().unsetHighlight().run();
    }
  };

  const isBgColorOpen = Boolean(bgColorAnchorEl);

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const isColorOpen = Boolean(colorAnchorEl);

  const openTableMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActiveMenu(null); // Close custom menus when native table menu opens
    setTableAnchorEl(event.currentTarget);
  };

  const closeTableMenu = () => {
    setTableAnchorEl(null);
  };

  const fontSizes = [
    "5px",
    "6px",
    "7px",
    "8px",
    "9px",
    "10px",
    "11px",
    "12px",
    "14px",
    "16px",
    "18px",
    "20px",
    "24px",
    "28px",
    "32px",
  ];
  const fonts = [
    "Aptos",
    "Calibri",
    "Arial",
    "Times New Roman",
    "Verdana",
    "Tahoma",
    "Georgia",
    "Courier New",
  ];
  const lineHeights = ["1.0", "1.15", "1.5", "1.8", "2.0", "2.5"];
  const letterSpacingOptions = ["0px", "1px", "2px", "3px", "4px", "5px"];
  // Central toggle helper
  const handleToggleMenu = (menu: MenuType) => {
    setActiveMenu((prev) => (prev === menu ? null : menu));
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Verify click was completely outside all custom components
      const clickedOutsideText =
        textMenuRef.current && !textMenuRef.current.contains(target);
      const clickedOutsideFont =
        fontMenuRef.current && !fontMenuRef.current.contains(target);
      const clickedOutsideFontSize =
        fontSizeMenuRef.current && !fontSizeMenuRef.current.contains(target);
      const clickedOutsLineHeight =
        lineHeightMenuRef.current &&
        !lineHeightMenuRef.current.contains(target);
      const clickedOutsideHeaderDirection =
        headerDirectionMenuRef.current &&
        !headerDirectionMenuRef.current.contains(target);
      const clickedOutsideLetterSpacing =
        letterSpacingMenuRef.current &&
        !letterSpacingMenuRef.current.contains(target);

      if (
        clickedOutsideText &&
        clickedOutsideFont &&
        clickedOutsideFontSize &&
        clickedOutsLineHeight &&
        clickedOutsideHeaderDirection &&
        clickedOutsideLetterSpacing
      ) {
        setActiveMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!editor) return null;

  const isCellSelection = editor.state.selection instanceof CellSelection;

  const getTextTypeLabel = () => {
    if (editor.isActive("heading", { level: 1 })) return "Heading 1";
    if (editor.isActive("heading", { level: 2 })) return "Heading 2";
    if (editor.isActive("heading", { level: 3 })) return "Heading 3";
    return "Normal Text";
  };

  const isTableHeaderSelected = () => {
    if (!editor) return false;

    const { selection } = editor.state;

    // CellSelection
    if (selection instanceof CellSelection) {
      const anchorCell = selection.$anchorCell.nodeAfter;
      const headCell = selection.$headCell.nodeAfter;

      return (
        anchorCell?.type.name === "tableHeader" ||
        headCell?.type.name === "tableHeader"
      );
    }

    // Normal cursor inside a table header
    for (let depth = selection.$from.depth; depth > 0; depth--) {
      const node = selection.$from.node(depth);

      if (node.type.name === "tableHeader") {
        return true;
      }
    }

    return false;
  };

  const setTableHeaderDirection = (direction: "horizontal" | "vertical") => {
    if (!editor) return;

    const { state, view } = editor;
    const { selection } = state;

    let tr = state.tr;
    let changed = false;

    // Multiple selected cells
    if (selection instanceof CellSelection) {
      const table = selection.$anchorCell.node(selection.$anchorCell.depth - 1);

      const tableDepth = selection.$anchorCell.depth - 1;
      const tableStart = selection.$anchorCell.start(tableDepth);
      const map = TableMap.get(table);

      const anchorRect = map.findCell(selection.$anchorCell.pos - tableStart);

      const headRect = map.findCell(selection.$headCell.pos - tableStart);

      const startRow = Math.min(anchorRect.top, headRect.top);

      const endRow = Math.max(anchorRect.bottom, headRect.bottom);

      const startColumn = Math.min(anchorRect.left, headRect.left);

      const endColumn = Math.max(anchorRect.right, headRect.right);

      for (let row = startRow; row < endRow; row++) {
        for (let column = startColumn; column < endColumn; column++) {
          const cellPos = map.positionAt(row, column, table);

          const cellNode = table.nodeAt(cellPos);

          if (!cellNode) continue;

          // Only modify table headers
          if (cellNode.type.name !== "tableHeader") {
            continue;
          }

          tr = tr.setNodeMarkup(tableStart + cellPos, undefined, {
            ...cellNode.attrs,
            textDirection: direction,
          });

          changed = true;
        }
      }
    } else {
      // Normal cursor inside a single table header
      for (let depth = selection.$from.depth; depth > 0; depth--) {
        const node = selection.$from.node(depth);

        if (node.type.name === "tableHeader") {
          const pos = selection.$from.before(depth);

          tr = tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            textDirection: direction,
          });

          changed = true;
          break;
        }
      }
    }

    if (!changed) {
      return;
    }

    view.dispatch(tr);
    view.focus();
  };

  const applyLink = () => {
    if (!linkUrl.trim()) {
      editor.chain().focus().unsetLink().run();
      setLinkDialogOpen(false);
      return;
    }

    if (editor.state.selection.empty && linkText) {
      editor
        .chain()
        .focus()
        .insertContent(linkText)
        .setTextSelection({
          from: editor.state.selection.from - linkText.length,
          to: editor.state.selection.from,
        })
        .setLink({
          href: linkUrl,
          target: openInNewTab ? "_blank" : "_self",
        })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({
          href: linkUrl,
          target: openInNewTab ? "_blank" : "_self",
        })
        .run();
    }
    setLinkDialogOpen(false);
  };

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (url) {
      editor?.chain().focus().setImage({ src: url }).run();
    }
  };

  const openImageMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (!isImageSelected) {
      toast.info(
        "Please select an image first to adjust its size or alignment.",
      );
      return;
    }
    setImageAnchorEl(event.currentTarget);
  };

  const closeImageMenu = () => {
    setImageAnchorEl(null);
  };

  const setImageSize = (percentage: number) => {
    if (!editor) return;

    const { state, view } = editor;
    const { selection } = state;

    if (
      !(selection instanceof NodeSelection) ||
      selection.node.type.name !== "image"
    ) {
      return;
    }

    const imagePos = selection.from;
    const imageNode = selection.node;

    // ---------------------------------------------------------
    // 1. Find the actual image DOM
    // ---------------------------------------------------------
    const nodeDOM = view.nodeDOM(imagePos) as HTMLElement | null;

    if (!nodeDOM) {
      console.warn("Image DOM not found");
      return;
    }

    const img =
      nodeDOM instanceof HTMLImageElement
        ? nodeDOM
        : nodeDOM.querySelector("img");

    if (!img) {
      console.warn("Image element not found");
      return;
    }

    // ---------------------------------------------------------
    // 2. Get a STABLE editor content width
    //    Do NOT use the image parent width.
    // ---------------------------------------------------------
    const editorDOM = view.dom as HTMLElement;

    const computedStyle = window.getComputedStyle(editorDOM);

    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

    const editorContentWidth =
      editorDOM.clientWidth - paddingLeft - paddingRight;

    if (editorContentWidth <= 0) {
      console.warn("Could not determine editor content width");
      return;
    }

    // ---------------------------------------------------------
    // 3. Calculate target width from the stable editor width
    // ---------------------------------------------------------
    const newWidth = Math.round(editorContentWidth * (percentage / 100));

    // ---------------------------------------------------------
    // 4. Preserve the original image aspect ratio
    // ---------------------------------------------------------
    let aspectRatio = 1;

    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      aspectRatio = img.naturalHeight / img.naturalWidth;
    } else {
      const currentWidth =
        Number(imageNode.attrs.width) || img.clientWidth || 1;

      const currentHeight =
        Number(imageNode.attrs.height) || img.clientHeight || 1;

      aspectRatio = currentHeight / currentWidth;
    }

    const newHeight = Math.round(newWidth * aspectRatio);

    // ---------------------------------------------------------
    // 5. Preserve unrelated styles
    // ---------------------------------------------------------
    const existingStyle = imageNode.attrs.style || "";

    const cleanedStyle = existingStyle
      .replace(/width\s*:\s*[^;]+;?/gi, "")
      .replace(/height\s*:\s*[^;]+;?/gi, "")
      .replace(/max-width\s*:\s*[^;]+;?/gi, "")
      .replace(/;+\s*;/g, ";")
      .replace(/^\s*;\s*|\s*;\s*$/g, "")
      .trim();

    // ---------------------------------------------------------
    // 6. Update TipTap image attributes
    // ---------------------------------------------------------
    const tr = state.tr.setNodeMarkup(imagePos, imageNode.type, {
      ...imageNode.attrs,

      // Store numeric pixel values
      width: newWidth,
      height: newHeight,

      // Keep existing unrelated styles
      style: cleanedStyle || null,
    });

    // Keep image selected
    tr.setSelection(NodeSelection.create(tr.doc, imagePos));

    tr.setMeta("imageSizeChanged", true);

    view.dispatch(tr);
    view.focus();

    closeImageMenu();
  };

  const setImageAlignment = (alignment: "left" | "center" | "right") => {
    if (!editor) return;

    const { state, view } = editor;
    const { selection } = state;

    if (
      !(selection instanceof NodeSelection) ||
      selection.node.type.name !== "image"
    ) {
      return;
    }

    const imagePos = selection.from;
    const imageNode = selection.node;

    const tr = state.tr.setNodeMarkup(imagePos, undefined, {
      ...imageNode.attrs,
      alignment,
    });

    const mappedPos = tr.mapping.map(imagePos);

    tr.setSelection(NodeSelection.create(tr.doc, mappedPos));

    view.dispatch(tr);
    view.focus();

    closeImageMenu();
  };

  const IMAGE_SIZE_PRESETS: Array<{
    label: string;
    width: number;
  }> = [
    { label: "25%", width: 25 },
    { label: "50%", width: 50 },
    { label: "75%", width: 75 },
    { label: "Full width", width: 100 },
  ];

  useEffect(() => {
    if (!editor) return;

    const updateImageSelection = () => {
      const { selection } = editor.state;

      const isImage =
        selection instanceof NodeSelection &&
        selection.node.type.name === "image";

      setIsImageSelected(isImage);
    };

    updateImageSelection();

    editor.on("selectionUpdate", updateImageSelection);
    editor.on("transaction", updateImageSelection);

    // 2. Clear image selection when clicking empty space or outside editor
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const isClickOnImage =
        target.tagName === "IMG" || Boolean(target.closest(".ProseMirror img"));
      const isClickOnMenuOrBtn =
        Boolean(target.closest(`.${classes.iconBtn}`)) ||
        Boolean(target.closest(`.${classes.menuPaper}`));

      if (!isClickOnImage && !isClickOnMenuOrBtn) {
        const { selection } = editor.state;
        if (
          selection instanceof NodeSelection &&
          selection.node.type.name === "image"
        ) {
          // Convert NodeSelection to TextSelection to deselect image in TipTap
          editor.commands.setTextSelection(selection.to);
        }
        setIsImageSelected(false);
        setImageAnchorEl(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      editor.off("selectionUpdate", updateImageSelection);
      editor.off("transaction", updateImageSelection);
    };
  }, [editor]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { authAxios } = useFetchContext();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (!files) return;
    let maxFigure = 0;

    editor.state.doc.descendants((node) => {
      if (node.type.name === "image" && node.attrs.caption) {
        const match = node.attrs.caption.match(/Figure\s+(\d+)/i);

        if (match) {
          maxFigure = Math.max(maxFigure, parseInt(match[1], 10));
        }
      }
    });

    for (const file of Array.from(files)) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await mutateAsset({
          authAxios,
          formData,
        });

        const imageUrl = response?.blob_url;

        if (imageUrl) {
          // Count existing captions to determine next figure number
          maxFigure++;

          const { state } = editor;
          const { selection } = state;

          const nodeAfter = selection.$from.nodeAfter;

          let chain = editor.chain().focus();

          if (nodeAfter?.type.name === "image") {
            chain = chain.setTextSelection(selection.from + nodeAfter.nodeSize);
          }

          chain
            .insertContent([
              {
                type: "image",
                attrs: {
                  src: imageUrl,
                  width: "800px",
                  height: "250px",
                  caption: `Figure ${maxFigure} : Report Image`,
                  style:
                    "margin: 0 !important; padding: 0 !important; display: block;",
                },
              },
            ])
            .run();
        }

        console.log("API Response:", response);

        toast.success(`${file.name} uploaded successfully`);
      } catch (error: any) {
        console.error("API Error:", error);

        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            `Failed to upload ${file.name}`,
        );
      }
    }

    event.target.value = "";
  };

  const [, forceUpdate] = useState({});
  const initialDocRef = useRef("");
  const [isEdited, setIsEdited] = useState(false);

  useEffect(() => {
    if (!editor) return;

    initialDocRef.current = JSON.stringify(editor.getJSON());
    setIsEdited(false);

    const update = () => {
      const currentDoc = JSON.stringify(editor.getJSON());
      setIsEdited(currentDoc !== initialDocRef.current);

      forceUpdate({});
    };

    const selectionUpdate = () => {
      forceUpdate({});
    };

    editor.on("update", update);
    editor.on("selectionUpdate", selectionUpdate);

    return () => {
      editor.off("update", update);
      editor.off("selectionUpdate", selectionUpdate);
    };
  }, [editor]);
  const canUndo =
    isEdited && (editor?.can().chain().focus().undo().run() ?? false);

  const canRedo = editor?.can().chain().focus().redo().run() ?? false;

  return (
    <MuiToolbar className={classes.root} disableGutters>
      {/* Left side actions */}
      <div className={classes.leftGroup}>
        {/* Undo */}
        <IconButton
          disabled={!canUndo}
          onClick={() => {
            if (!canUndo) return;
            editor.chain().focus().undo().run();
          }}
        >
          <UndoIcon
            style={{
              color: canUndo ? "#fff" : "#666",
              fontSize: 18,
            }}
          />
        </IconButton>
        {/* {Redo} */}
        <IconButton
          disabled={!canRedo}
          onClick={() => {
            if (!canRedo) return;
            editor.chain().focus().redo().run();
          }}
        >
          <RedoIcon
            style={{
              color: canRedo ? "#fff" : "#666",
              fontSize: 18,
            }}
          />
        </IconButton>
        <Divider orientation="vertical" flexItem className={classes.divider} />
        {/* Zoom */}
        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={() => setZoom((z) => Math.max(50, z - 10))}
        >
          <RemoveIcon style={{ fontSize: 16 }} />
        </IconButton>

        <Typography className={classes.zoomText}>{zoom}%</Typography>

        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={() => setZoom((z) => Math.min(200, z + 10))}
        >
          <AddIcon style={{ fontSize: 16 }} />
        </IconButton>
        <Divider orientation="vertical" flexItem className={classes.divider} />

        {/* Text Node Type Dropdown */}
        <div style={{ position: "relative" }} ref={textMenuRef}>
          <Button
            className={classes.textMenuBtn}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16 }} />}
            onClick={() => handleToggleMenu("text")}
          >
            {getTextTypeLabel()}
          </Button>
          {activeMenu === "text" && (
            <div className={classes.dropdownMenu}>
              <div
                className={classes.dropdownItem}
                onClick={() => {
                  editor.chain().focus().setParagraph().run();
                  setActiveMenu(null);
                }}
              >
                Normal Text
              </div>
              <div
                className={classes.dropdownItem}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 1 }).run();
                  setActiveMenu(null);
                }}
              >
                Heading 1
              </div>
              <div
                className={classes.dropdownItem}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: 2 }).run();
                  setActiveMenu(null);
                }}
              >
                Heading 2
              </div>
            </div>
          )}
        </div>

        {/* Font Family Dropdown */}
        <div style={{ position: "relative" }} ref={fontMenuRef}>
          <Button
            className={classes.textMenuBtn}
            endIcon={<ArrowDropDownIcon />}
            onClick={() => handleToggleMenu("font")}
          >
            Font
          </Button>

          {activeMenu === "font" && (
            <div className={classes.dropdownMenu}>
              {fonts.map((font) => (
                <div
                  key={font}
                  className={classes.dropdownItem}
                  style={{ fontFamily: font }}
                  onClick={() => {
                    editor.chain().focus().setFontFamily(font).run();
                    setActiveMenu(null);
                  }}
                >
                  {font}
                </div>
              ))}
            </div>
          )}
        </div>
        <Divider orientation="vertical" flexItem className={classes.divider} />

        {/* Font Size Dropdown */}
        <div style={{ position: "relative" }} ref={fontSizeMenuRef}>
          <Button
            className={classes.textMenuBtn}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16 }} />}
            onClick={() => handleToggleMenu("fontSize")}
          >
            Font Size
          </Button>

          {activeMenu === "fontSize" && (
            <div className={classes.dropdownMenu}>
              {fontSizes.map((size) => (
                <div
                  key={size}
                  className={classes.dropdownItem}
                  onClick={() => {
                    editor.chain().focus().setFontSize(size).run();
                    setActiveMenu(null);
                  }}
                >
                  {size}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ position: "relative" }} ref={lineHeightMenuRef}>
          <Button
            className={classes.textMenuBtn}
            startIcon={<FormatLineSpacingIcon style={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16 }} />}
            onClick={() => handleToggleMenu("lineHeight")}
          >
            Spacing
          </Button>

          {activeMenu === "lineHeight" && (
            <div className={classes.dropdownMenu}>
              {lineHeights.map((spacing) => (
                <div
                  key={spacing}
                  className={classes.dropdownItem}
                  onClick={() => {
                    editor.chain().focus().setLineHeight(spacing).run();
                    setActiveMenu(null);
                  }}
                >
                  {spacing}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Table Header Text Direction */}
        <div style={{ position: "relative" }} ref={headerDirectionMenuRef}>
          <Button
            className={classes.textMenuBtn}
            startIcon={<ViewColumnIcon style={{ fontSize: 16 }} />}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16 }} />}
            onClick={() => handleToggleMenu("headerDirection")}
          ></Button>

          {activeMenu === "headerDirection" && (
            <div className={classes.dropdownMenu}>
              <div
                className={classes.dropdownItem}
                onClick={() => {
                  setTableHeaderDirection("horizontal");
                  setActiveMenu(null);
                }}
              >
                Horizontal
              </div>

              <div
                className={classes.dropdownItem}
                onClick={() => {
                  setTableHeaderDirection("vertical");
                  setActiveMenu(null);
                }}
              >
                Vertical
              </div>
            </div>
          )}
        </div>

        <Divider orientation="vertical" flexItem className={classes.divider} />

        {/* Inline Toggles */}
        <IconButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`${classes.iconBtn} ${editor.isActive("bold") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatBoldIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`${classes.iconBtn} ${editor.isActive("italic") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatItalicIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`${classes.iconBtn} ${editor.isActive("underline") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatUnderlinedIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          className={`${classes.iconBtn} ${editor.isActive("strike") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatStrikethroughIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={`${classes.iconBtn} ${editor.isActive("code") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <CodeIcon style={{ fontSize: 18 }} />
        </IconButton>

        <div>
          <Button
            className={classes.textMenuBtn}
            endIcon={<ArrowDropDownIcon style={{ fontSize: 16 }} />}
            onClick={handleColorClick}
          >
            <span
              style={{
                color: "#ffffff",
                borderBottom: `2px solid ${editor.getAttributes("textStyle")?.color || "#ffffff"}`,
                fontWeight: 700,
                paddingBottom: "1px",
              }}
            >
              A
            </span>
          </Button>

          <Popover
            open={isColorOpen}
            anchorEl={colorAnchorEl}
            onClose={handleColorClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ className: classes.colorDropdownMenu }}
          >
            <SketchPicker
              color={pickerColor}
              onChange={(color) => setPickerColor(color.hex)}
              onChangeComplete={(color) => {
                editor.chain().focus().setColor(color.hex).run();
              }}
              disableAlpha={false}
              presetColors={[
                "#FF5733",
                "#FF0000",
                "#F0F0E0",
                "#FFFF00",
                "#00FF00",
                "#00FFFF",
                "#0054C5",
                "#800080",
                "#FFFFFF",
                "#000000",
              ]}
            />
          </Popover>
        </div>
        <div>
          <IconButton
            className={`${classes.iconBtn} ${editor.isActive("highlight") ? classes.activeIconBtn : ""}`}
            size="small"
            onClick={handleTextBgColorClick}
          >
            <HighlightIcon style={{ fontSize: 18 }} />
          </IconButton>

          {/* Color Picker Popover */}
          <Popover
            open={Boolean(textBgColorAnchorEl)}
            anchorEl={textBgColorAnchorEl}
            onClose={handleTextBgColorClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <SketchPicker
              color={textBgColor}
              onChange={(color) => setTextBgColor(color.hex)}
              onChangeComplete={(color) => applyTextHighlight(color.hex)}
              disableAlpha={false}
              presetColors={[
                "#FF5733",
                "#FF0000",
                "#F0F0E0",
                "#FFFF00",
                "#00FF00",
                "#00FFFF",
                "#0054C5",
                "#800080",
                "#FFFFFF",
                "#000000",
              ]}
            />
          </Popover>
        </div>
        {/* Table Cell Background Color */}

        <div>
          <Button
            className={classes.textMenuBtn}
            onMouseDown={(event) => {
              /*
               * Save the current CellSelection before
               * the button takes focus.
               */
              event.preventDefault();
              saveCurrentCellSelection();
            }}
            onClick={handleBgColorClick}
          >
            <span
              style={{
                display: "inline-block",
                width: 20,
                height: 20,
                backgroundColor: bgPickerColor,
                border: "1px solid #888",
                borderRadius: 3,
              }}
            />

            <ArrowDropDownIcon style={{ fontSize: 16 }} />
          </Button>

          <Popover
            open={Boolean(bgColorAnchorEl)}
            anchorEl={bgColorAnchorEl}
            onClose={handleBgColorClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <SketchPicker
              color={bgPickerColor}
              onChange={(color) => {
                setBgPickerColor(color.hex);
              }}
              onChangeComplete={(color) => {
                applyTableBackgroundColor(color.hex);
                handleBgColorClose();
              }}
              disableAlpha={false}
              presetColors={[
                "#FF5733",
                "#FF0000",
                "#F0F0E0",
                "#FFFF00",
                "#00FF00",
                "#00FFFF",
                "#0054C5",
                "#800080",
                "#FFFFFF",
                "#000000",
              ]}
            />
          </Popover>
        </div>

        <Divider orientation="vertical" flexItem className={classes.divider} />

        {/* Link & Media */}
        <IconButton
          onClick={() => fileInputRef.current?.click()}
          className={`${classes.iconBtn} ${
            editor.isActive("link") ? classes.activeIconBtn : ""
          }`}
          size="small"
        >
          <LinkIcon />
        </IconButton>
        <Tooltip title="Insert Button" arrow>
          <IconButton
            onClick={onInsertButton}
            className={classes.iconBtn}
            size="small"
          >
            <AddCircleOutlinedIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {/* Insert Images into TipTap */}
        <IconButton onClick={addImage} className={classes.iconBtn} size="small">
          <ImageIcon style={{ fontSize: 18 }} />
        </IconButton>

        {/* Image resize/alignment options (applies to the selected image) */}
        <Tooltip title="Image Resize & alignment" arrow>
          <span>
            <IconButton
              onClick={openImageMenu}
              className={`${classes.iconBtn} ${
                isImageSelected ? classes.activeIconBtn : ""
              }`}
              size="small"
            >
              <img
                src="/Resize%20icon.svg"
                alt="Resize"
                style={{
                  width: 18,
                  height: 18,
                  display: "block",
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        <Menu
          anchorEl={imageAnchorEl}
          open={Boolean(imageAnchorEl)}
          onClose={closeImageMenu}
          keepMounted
          PaperProps={{
            className: classes.menuPaper,
          }}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <MenuItem disabled className={classes.menuItemHeader}>
            Size
          </MenuItem>

          {IMAGE_SIZE_PRESETS.map((preset) => (
            <MenuItem
              key={preset.width}
              className={classes.menuItem}
              onClick={() => setImageSize(preset.width)}
            >
              {preset.label}
            </MenuItem>
          ))}

          <Divider className={classes.menuDivider} />

          <MenuItem disabled className={classes.menuItemHeader}>
            Alignment
          </MenuItem>

          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              setImageAlignment("left");
              closeImageMenu();
            }}
          >
            Left
          </MenuItem>

          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              setImageAlignment("center");
              closeImageMenu();
            }}
          >
            Center
          </MenuItem>

          <MenuItem
            className={classes.menuItem}
            onClick={() => {
              setImageAlignment("right");
              closeImageMenu();
            }}
          >
            Right
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" flexItem className={classes.divider} />

        {/* Lists & Alignment */}
        <IconButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`${classes.iconBtn} ${editor.isActive("bulletList") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatListBulletedIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`${classes.iconBtn} ${editor.isActive("orderedList") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatListNumberedIcon style={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          onClick={() => handleAlignment("left")}
          className={`${classes.iconBtn} ${isAligned("left") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatAlignLeftIcon style={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          onClick={() => handleAlignment("center")}
          className={`${classes.iconBtn} ${isAligned("center") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatAlignCenterIcon style={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          onClick={() => handleAlignment("right")}
          className={`${classes.iconBtn} ${isAligned("right") ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatAlignRightIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          onClick={() =>
            editor
              .chain()
              .focus()
              .setTextAlign(
                editor.isActive({ textAlign: "justify" }) ? "left" : "justify",
              )
              .run()
          }
          className={`${classes.iconBtn} ${editor.isActive({ textAlign: "justify" }) ? classes.activeIconBtn : ""}`}
          size="small"
        >
          <FormatAlignJustifyIcon style={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={() => {
            const { state } = editor;
            const { $from } = state.selection;

            for (let depth = $from.depth; depth > 0; depth--) {
              const node = $from.node(depth);

              if (
                node.type.name === "paragraph" ||
                node.type.name === "heading"
              ) {
                const currentIndent = node.attrs.indent || 0;

                const newIndent = currentIndent + 48;

                editor
                  .chain()
                  .focus()
                  .command(({ tr }) => {
                    tr.setNodeMarkup($from.before(depth), undefined, {
                      ...node.attrs,
                      indent: newIndent,
                    });

                    return true;
                  })
                  .run();

                break;
              }
            }
          }}
        >
          <FormatIndentIncreaseIcon style={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={() => {
            const { state } = editor;
            const { $from } = state.selection;

            for (let depth = $from.depth; depth > 0; depth--) {
              const node = $from.node(depth);

              if (
                node.type.name === "paragraph" ||
                node.type.name === "heading"
              ) {
                const currentIndent = node.attrs.indent || 0;

                // Never allow negative indentation
                const newIndent = Math.max(0, currentIndent - 48);

                editor
                  .chain()
                  .focus()
                  .command(({ tr }) => {
                    tr.setNodeMarkup($from.before(depth), undefined, {
                      ...node.attrs,
                      indent: newIndent,
                    });

                    return true;
                  })
                  .run();

                break;
              }
            }
          }}
        >
          <FormatIndentDecreaseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </div>

      {/* Letter Spacing */}

      <div style={{ position: "relative" }} ref={letterSpacingMenuRef}>
        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={() => handleToggleMenu("letterSpacing")}
        >
          <img
            src="/Format%20Letter%20Spacing%20Icon.svg"
            alt="Letter Spacing"
            style={{ width: 18, height: 18, display: "block" }}
          />
        </IconButton>

        {activeMenu === "letterSpacing" && (
          <div className={classes.dropdownMenu}>
            {letterSpacingOptions.map((spacing) => (
              <div
                key={spacing}
                className={classes.dropdownItem}
                onClick={() => {
                  if (spacing === "0px") {
                    editor.chain().focus().unsetLetterSpacing().run();
                  } else {
                    editor.chain().focus().setLetterSpacing(spacing).run();
                  }

                  setActiveMenu(null);
                }}
              >
                {spacing === "0px" ? "Normal" : spacing}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right side utility menus */}
      <div className={classes.rightGroup}>
        <Tooltip title="Navigate sections" arrow>
          <IconButton
            className={classes.iconBtn}
            size="small"
            onClick={() => onOpenNavPane?.()}
          >
            <TocIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title="Find & Replace" arrow>
          <IconButton
            className={classes.iconBtn}
            size="small"
            onClick={onOpenFindReplace}
          >
            <FindReplaceIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete Page" arrow>
          <IconButton
            className={classes.iconBtn}
            size="small"
            onClick={onDeletePage}
            onMouseDown={(e) => e.preventDefault()}
          >
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Save Document" arrow>
          <IconButton onClick={onSave} className={classes.iconBtn} size="small">
            <SaveIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <IconButton
          className={classes.iconBtn}
          size="small"
          onClick={toggleTheme}
        >
          {darkMode ? (
            <Brightness7Icon style={{ fontSize: 18 }} />
          ) : (
            <Brightness4Icon style={{ fontSize: 18 }} />
          )}
        </IconButton>
      </div>

      <Tooltip title="Print" arrow>
        <IconButton onClick={onPrint} className={classes.iconBtn} size="small">
          <PrintIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <div style={{ position: "relative" }}>
        <Button
          className={classes.textMenuBtn}
          startIcon={<TableChartIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={openTableMenu}
        >
          Table
        </Button>

        <Menu
          anchorEl={tableAnchorEl}
          open={Boolean(tableAnchorEl)}
          onClose={closeTableMenu}
          keepMounted
        >
          <MenuItem
            onClick={() => {
              editor
                ?.chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run();
              closeTableMenu();
            }}
          >
            Insert Table
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().addRowBefore().run();
              closeTableMenu();
            }}
          >
            Insert Row Above
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().addRowAfter().run();
              closeTableMenu();
            }}
          >
            Insert Row Below
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().deleteRow().run();
              closeTableMenu();
            }}
          >
            Delete Row
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().addColumnBefore().run();
              closeTableMenu();
            }}
          >
            Insert Column Left
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().addColumnAfter().run();
              closeTableMenu();
            }}
          >
            Insert Column Right
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().deleteColumn().run();
              closeTableMenu();
            }}
          >
            Delete Column
          </MenuItem>
          <MenuItem
            disabled={!editor.isActive("table")}
            onClick={() => {
              editor.chain().focus().deleteTable().run();
              closeTableMenu();
            }}
          >
            Delete Table
          </MenuItem>
          <MenuItem
            disabled={!editor.can().mergeCells()}
            onClick={() => {
              editor.chain().focus().mergeCells().run();
              closeTableMenu();
            }}
          >
            Merge Cells
          </MenuItem>
          <MenuItem
            disabled={!editor.can().splitCell()}
            onClick={() => {
              editor.chain().focus().splitCell().run();
              closeTableMenu();
            }}
          >
            Split Cell
          </MenuItem>
        </Menu>
      </div>

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <TextField
            label="Display Text"
            fullWidth
            margin="normal"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
          />
          <TextField
            label="URL"
            fullWidth
            margin="normal"
            value={linkUrl}
            placeholder="https://example.com"
            onChange={(e) => setLinkUrl(e.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={openInNewTab}
                onChange={(e) => setOpenInNewTab(e.target.checked)}
                color="primary"
              />
            }
            label="Open in new tab"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)} color="default">
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={applyLink}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </MuiToolbar>
  );
}
